<div class="mcp-shell">
  <section class="hero-card">
    <div class="hero-copy">
      <div class="eyebrow">Auto fetch</div>
      <h1>MCP Reports</h1>
      <p>Fetch the market snapshot from IEX, preserve the website row structure in the stored CSV, and preview the saved report with market-specific columns.</p>
    </div>
    <div class="hero-icon">
      <lucide-icon [img]="CalendarRangeIcon" [size]="28"></lucide-icon>
    </div>
  </section>

  <section class="action-card">
    <div class="form-grid">
      <label>
        <span>Market</span>
        <select [ngModel]="form().product" (ngModelChange)="updateField('product', $event)">
          @for (product of productOptions; track product) {
            <option [value]="product">{{ product }}</option>
          }
        </select>
      </label>
      <label>
        <span>From date</span>
        <input type="date" [ngModel]="form().from_date" (ngModelChange)="updateField('from_date', $event)" />
      </label>
      <label>
        <span>To date</span>
        <input type="date" [ngModel]="form().to_date" (ngModelChange)="updateField('to_date', $event)" />
      </label>
      <button type="button" class="primary-btn" [disabled]="fetching()" (click)="fetchReport()">
        <lucide-icon [img]="DownloadIcon" [size]="16"></lucide-icon>
        <span>{{ fetching() ? 'Fetching...' : 'Fetch and Save CSV' }}</span>
      </button>
    </div>
    <div class="hint">Use the market switch to focus on one exchange product. Explicit dates are fetched as a range exactly as selected.</div>
  </section>

  <div class="content-grid">
    <section class="list-card">
      <div class="section-head">
        <h2>Saved Reports</h2>
        <span>{{ reports().length }} {{ form().product }} reports</span>
      </div>

      @if (loading()) {
        <div class="empty-state">Loading reports...</div>
      } @else if (reports().length === 0) {
        <div class="empty-state">No {{ form().product }} MCP reports fetched yet.</div>
      } @else {
        <div class="report-list">
          @for (report of reports(); track report.id) {
            <div class="report-item" [class.active]="activeReportId() === report.id" (click)="selectReport(report.id)">
              <div class="report-copy">
                <strong>{{ report.product }} • {{ report.report_from_date }} to {{ report.report_to_date }}</strong>
                <span>{{ report.row_count }} rows • {{ report.interval }}</span>
              </div>
              <div class="report-actions" (click)="$event.stopPropagation()">
                <button type="button" class="icon-btn" title="Download CSV" (click)="downloadReport(report)">
                  <lucide-icon [img]="DownloadIcon" [size]="15"></lucide-icon>
                </button>
                <button type="button" class="icon-btn" title="Re-download" [disabled]="isRefetching(report.id)" (click)="refetchReport(report)">
                  <lucide-icon [img]="RefreshCcwIcon" [size]="15"></lucide-icon>
                </button>
              </div>
            </div>
          }
        </div>
      }
    </section>

    <section class="detail-card">
      <div class="section-head">
        <h2>Report Preview</h2>
        @if (selectedReport()) {
          <span>{{ selectedReport()?.original_name }}</span>
        }
      </div>

      @if (!selectedReport()) {
        <div class="empty-state">Select a saved report to preview the stored database rows.</div>
      } @else {
        <div class="summary-strip">
          <div>
            <small>Market</small>
            <strong>{{ selectedReport()?.product }}</strong>
          </div>
          <div>
            <small>Source range</small>
            <strong>{{ selectedReport()?.report_from_date }} to {{ selectedReport()?.report_to_date }}</strong>
          </div>
          <div>
            <small>Rows stored</small>
            <strong>{{ selectedReport()?.row_count }}</strong>
          </div>
          <div>
            <small>Last fetched</small>
            <strong>{{ selectedReport()?.last_refetched_at || selectedReport()?.created_at }}</strong>
          </div>
        </div>

        @if (selectedSummary().length > 0) {
          <div class="summary-table">
            @for (item of selectedSummary(); track $index) {
              <div class="summary-chip">
                <span>{{ item['period'] || 'Summary' }}</span>
                <strong>{{ item['mcp'] || item['purchase_bid'] || '-' }}</strong>
              </div>
            }
          </div>
        }

        <div class="table-wrap">
          <div style="max-height: 500px; overflow-y: auto;">
            @if (siteColumns().length > 0 && siteRows().length > 0) {
              <table>
                <thead>
                  <tr>
                    @for (column of siteColumns(); track column) {
                      <th>{{ formatColumnLabel(column) }}</th>
                    }
                  </tr>
                </thead>
                <tbody>
                  @for (row of siteRows(); track $index) {
                    <tr>
                      @for (column of siteColumns(); track column) {
                        <td>{{ row[column] ?? '-' }}</td>
                      }
                    </tr>
                  }
                </tbody>
              </table>
            } @else {
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Period</th>
                    <th>Purchase Bid</th>
                    <th>Sell Bid</th>
                    <th>MCV</th>
                    <th>Final Scheduled Volume</th>
                    <th>MCP</th>
                    <th>Congestion</th>
                  </tr>
                </thead>
                <tbody>
                  @for (row of selectedReport()?.rows || []; track row.id) {
                    <tr>
                      <td>{{ row.report_date }}</td>
                      <td>{{ row.period }}</td>
                      <td>{{ row.purchase_bid || '-' }}</td>
                      <td>{{ row.sell_bid || '-' }}</td>
                      <td>{{ row.mcv || '-' }}</td>
                      <td>{{ row.final_scheduled_volume || '-' }}</td>
                      <td>{{ row.mcp || '-' }}</td>
                      <td>{{ row.congestion || '-' }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            }
          </div>
        </div>
      }
    </section>
  </div>
</div>
